import type { BorderBottomStyleProperty, BorderLeftStyleProperty, BorderRightStyleProperty, BorderTopStyleProperty } from "@rtsao/csstype";
import type { Border, Font, Typography } from "baseui/theme";
import type { StyleObject } from "styletron-standard";
import type { DeepPartial } from "ts-essentials";

export type PrimitiveFont = Partial<Font> & {
  primaryFont?: Partial<Font>;
  monoFont?: Partial<Font>;
};

export interface PrimitiveTypography {
  primaryFontFamily?: string;
  monoFontFamily?: string;
  typography?: {
    ParagraphXSmall?: PrimitiveFont;
    ParagraphSmall?: PrimitiveFont;
    ParagraphMedium?: PrimitiveFont;
    ParagraphLarge?: PrimitiveFont;
    LabelXSmall?: PrimitiveFont;
    LabelSmall?: PrimitiveFont;
    LabelMedium?: PrimitiveFont;
    LabelLarge?: PrimitiveFont;
    HeadingXSmall?: PrimitiveFont;
    HeadingSmall?: PrimitiveFont;
    HeadingMedium?: PrimitiveFont;
    HeadingLarge?: PrimitiveFont;
    HeadingXLarge?: PrimitiveFont;
    HeadingXXLarge?: PrimitiveFont;
    DisplayXSmall?: PrimitiveFont;
    DisplaySmall?: PrimitiveFont;
    DisplayMedium?: PrimitiveFont;
    DisplayLarge?: PrimitiveFont;
  };
}

// Derived from https://github.com/gadget-inc/baseweb/blob/build-1b4286d28/themes/shared/typography.js#L155-L172
export const PrimitiveTypographyMap: Record<keyof NonNullable<PrimitiveTypography["typography"]>, string> = {
  ParagraphXSmall: "font100",
  ParagraphSmall: "font200",
  ParagraphMedium: "font300",
  ParagraphLarge: "font400",
  LabelXSmall: "font150",
  LabelSmall: "font250",
  LabelMedium: "font350",
  LabelLarge: "font450",
  HeadingXSmall: "font550",
  HeadingSmall: "font650",
  HeadingMedium: "font750",
  HeadingLarge: "font850",
  HeadingXLarge: "font950",
  HeadingXXLarge: "font1050",
  DisplayXSmall: "font1150",
  DisplaySmall: "font1250",
  DisplayMedium: "font1350",
  DisplayLarge: "font1450",
};

export const DISCORD_INVITE_URL = "https://ggt.link/discord";

/**
 * BaseUI has a bunch of redundant typography styles that can be
 * derived from a smaller set of primitives. This helper generates
 * those redundant styles.
 */
export const generateTypography = (primitive: PrimitiveTypography): DeepPartial<Typography> =>
  Object.fromEntries(
    Object.entries(PrimitiveTypographyMap).flatMap(([name, fontCode]) => {
      const {
        primaryFont: primitivePrimaryFont = {},
        monoFont: primitiveMonoFont = {},
        ...primitiveBaseFont
      } = primitive.typography ? primitive.typography[name as keyof typeof PrimitiveTypographyMap] ?? {} : {};

      const primaryFont = {
        ...(primitive.primaryFontFamily ? { fontFamily: primitive.primaryFontFamily } : {}),
        ...primitiveBaseFont,
        ...primitivePrimaryFont,
      };

      const monoFont = {
        ...(primitive.monoFontFamily ? { fontFamily: primitive.monoFontFamily } : {}),
        ...primitiveBaseFont,
        ...primitiveMonoFont,
      };

      return [
        [fontCode, primaryFont],
        [name, primaryFont],
        [`Mono${name}`, monoFont],
      ];
    })
  );

const nullBorder: Border = {
  borderColor: "transparent",
  borderStyle: "solid",
  borderWidth: "0px",
};

export const borderRadiiTop = (borderRadius: string): StyleObject => {
  return {
    borderTopLeftRadius: borderRadius,
    borderTopRightRadius: borderRadius,
  };
};

export const borderRadiiRight = (borderRadius: string): StyleObject => {
  return {
    borderTopRightRadius: borderRadius,
    borderBottomRightRadius: borderRadius,
  };
};

/**
 *
 * @param border
 * @returns { borderRightWidth, borderRightStyle, borderRightColor }
 */
export const expandBorderRight = (border: Border | "none"): Record<string, string> => {
  if (border === "none") return expandBorderRight(nullBorder);
  return {
    borderRightWidth: border.borderWidth,
    borderRightStyle: border.borderStyle as BorderRightStyleProperty,
    borderRightColor: border.borderColor,
  };
};

/**
 *
 * @param border
 * @returns { borderLeftWidth, borderLeftStyle, borderLeftColor }
 */
export const expandBorderLeft = (border: Border | "none"): Record<string, string> => {
  if (border === "none") return expandBorderLeft(nullBorder);
  return {
    borderLeftWidth: border.borderWidth,
    borderLeftStyle: border.borderStyle as BorderLeftStyleProperty,
    borderLeftColor: border.borderColor,
  };
};

/**
 *
 * @param border
 * @returns borderTopWidth, borderTopStyle, borderTopColor
 */
export const expandBorderTop = (border: Border | "none"): Record<string, string> => {
  if (border === "none") return expandBorderTop(nullBorder);
  return {
    borderTopWidth: border.borderWidth,
    borderTopStyle: border.borderStyle as BorderTopStyleProperty,
    borderTopColor: border.borderColor,
  };
};

/**
 *
 * @param border
 * @returns { borderBottomWidth, borderBottomStyle, borderBottomColor }
 */
export const expandBorderBottom = (border: Border | "none"): Record<string, string> => {
  if (border === "none") return expandBorderBottom(nullBorder);
  return {
    borderBottomWidth: border.borderWidth,
    borderBottomStyle: border.borderStyle as BorderBottomStyleProperty,
    borderBottomColor: border.borderColor,
  };
};

export const borderRadiiBottom = (borderRadius: StyleObject["borderRadius"]): StyleObject => {
  return {
    borderBottomLeftRadius: borderRadius,
    borderBottomRightRadius: borderRadius,
  };
};

export const borderRadiiLeft = (borderRadius: StyleObject["borderRadius"]): StyleObject => {
  return {
    borderTopLeftRadius: borderRadius,
    borderBottomLeftRadius: borderRadius,
  };
};

export const expandBorderRadii = (borderRadius: StyleObject["borderRadius"]): StyleObject => {
  return {
    borderTopLeftRadius: borderRadius,
    borderBottomLeftRadius: borderRadius,
    borderTopRightRadius: borderRadius,
    borderBottomRightRadius: borderRadius,
  };
};

export const expandBorderColors = (borderColor: StyleObject["borderColor"]): StyleObject => {
  return {
    borderTopColor: borderColor,
    borderRightColor: borderColor,
    borderBottomColor: borderColor,
    borderLeftColor: borderColor,
  };
};

export const expandBorderStyles = (borderStyle: string | undefined): StyleObject => {
  return {
    borderTopStyle: borderStyle as StyleObject["borderTopStyle"],
    borderRightStyle: borderStyle as StyleObject["borderTopStyle"],
    borderBottomStyle: borderStyle as StyleObject["borderTopStyle"],
    borderLeftStyle: borderStyle as StyleObject["borderTopStyle"],
  };
};

export const expandBorderWidth = (borderWidth: StyleObject["borderWidth"]): StyleObject => {
  return {
    borderTopWidth: borderWidth,
    borderRightWidth: borderWidth,
    borderBottomWidth: borderWidth,
    borderLeftWidth: borderWidth,
  };
};

export const expandBaseWebBorder = (border: Border) => {
  return {
    ...expandBorderWidth(border.borderWidth),
    ...expandBorderColors(border.borderColor),
    ...expandBorderStyles(border.borderStyle),
  };
};

/**
 *
 * @param margin
 * @returns { marginTop, marginLeft, marginRight, marginBottom }
 */
export const expandMargin = (margin: string | number) => ({
  marginTop: margin,
  marginRight: margin,
  marginBottom: margin,
  marginLeft: margin,
});

/**
 *
 * @param padding
 * @returns { paddingTop, paddingLeft, paddingRight, paddingBottom }
 */
export const expandPadding = (padding: string | number) => ({
  paddingTop: padding,
  paddingRight: padding,
  paddingBottom: padding,
  paddingLeft: padding,
});

/**
 * @param margin
 * @param [margin2=margin]
 * @returns { marginLeft, marginRight }
 */
export const marginHorizontal = (margin: string | number, margin2 = margin) => ({
  marginLeft: margin,
  marginRight: margin2,
});

/**
 *
 * @param margin
 * @param [margin2=margin]
 * @returns { marginTop, marginBottom }
 */
export const marginVertical = (margin: string | number, margin2 = margin) => ({
  marginTop: margin,
  marginBottom: margin2,
});

/**
 * @param padding
 * @param [padding2=padding]
 * @returns { paddingLeft, paddingRight }
 */
export const paddingHorizontal = (padding: string | number, padding2 = padding) => ({
  paddingLeft: padding,
  paddingRight: padding2,
});

/**
 *
 * @param padding
 * @param [padding2=padding]
 * @returns { paddingTop, paddingBottom }
 */
export const paddingVertical = (padding: string | number, padding2 = padding) => ({
  paddingTop: padding,
  paddingBottom: padding2,
});

/**
 * Use when you know padding sizes you want to set for all 4 sides of an element
 *
 * @param padding 1-4 size arguments (same order as CSS shorthand)
 * @returns { paddingTop, paddingLeft, paddingRight, paddingBottom }
 */
export const paddingUtil = (...padding: Array<string | number>) => {
  const [paddingArg1 = 0, paddingarg2, paddingArg3, paddingArg4] = padding;
  const paddingTop = paddingArg1;
  let paddingRight = paddingArg1,
    paddingBottom = paddingArg1,
    paddingLeft = paddingArg1;

  if (typeof paddingarg2 !== "undefined") {
    paddingRight = paddingarg2;
    paddingLeft = paddingarg2;
    if (typeof paddingArg3 !== "undefined") {
      paddingBottom = paddingArg3;
      if (typeof paddingArg4 !== "undefined") {
        paddingLeft = paddingArg4;
      }
    }
  }

  return {
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
  };
};

/**
 * Use when you know the margin sizes you want to set for all 4 sides of an element
 *
 * @param margin 1-4 size arguments (same order as CSS shorthand)
 * @returns { marginTop, marginLeft, marginRight, marginBottom }
 */
export const marginUtil = (...margin: Array<string | number>) => {
  const [marginArg1 = 0, marginarg2, marginArg3, marginArg4] = margin;
  const marginTop = marginArg1;
  let marginRight = marginArg1,
    marginBottom = marginArg1,
    marginLeft = marginArg1;

  if (typeof marginarg2 !== "undefined") {
    marginRight = marginarg2;
    marginLeft = marginarg2;
    if (typeof marginArg3 !== "undefined") {
      marginBottom = marginArg3;
      if (typeof marginArg4 !== "undefined") {
        marginLeft = marginArg4;
      }
    }
  }

  return {
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
  };
};
